/**
 * useSkinReveal — global trigger + state for the cinematic skin-evolution
 * reveal (Pokémon-style "Djay evolves into a new look!").
 *
 * Why a module-level store (mirrors useDjaySelection / useEasterEggs):
 * the reveal must be fireable from ANY code path, including ones that live
 * OUTSIDE a practice session — most importantly the `DJ Goud` long-press
 * easter egg, which is triggered from the always-mounted FloatingDjay buddy.
 * A single global store means the reveal modal can be mounted once at the app
 * shell and any caller can `triggerSkinReveal(...)` without prop-drilling.
 *
 * The modal (SkinEvolutionReveal) subscribes via useSyncExternalStore.
 */

import { useCallback, useSyncExternalStore } from 'react'
import type { DjayVariant } from '../DjayAvatar'

export interface SkinRevealRequest {
  /** The newly unlocked variant to evolve INTO. */
  variant: DjayVariant
  /**
   * The variant to morph FROM. Defaults to 'default' so the evolution always
   * reads as "base Djay → new look", which is the strongest visual contrast.
   */
  from?: DjayVariant
  /** Optional override copy for the eyebrow line (defaults to "Nieuwe look!"). */
  eyebrow?: string
}

type Listener = () => void

let _active: SkinRevealRequest | null = null
const _listeners = new Set<Listener>()

function getSnapshot(): SkinRevealRequest | null {
  return _active
}

function subscribe(listener: Listener): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

function _notify() {
  _listeners.forEach((l) => l())
}

/**
 * Fire the cinematic reveal. Safe to call from anywhere (in-session or the
 * standalone easter-egg path). Ignores a re-trigger while one is already
 * playing so we never stack two evolutions.
 */
export function triggerSkinReveal(request: SkinRevealRequest): void {
  if (_active) return
  _active = { from: 'default', ...request }
  _notify()
}

function _clear() {
  if (_active === null) return
  _active = null
  _notify()
}

export function useSkinReveal() {
  const active = useSyncExternalStore(subscribe, getSnapshot)
  const dismiss = useCallback(() => _clear(), [])
  return { active, dismiss }
}
